/**
 * Move enumeration and application.
 *
 *   legalMoves(state, uid, mrxStation?)  -> all legal Move values for that uid
 *   applyMove(state, move, mrxStation)   -> next state + next mrxStation
 *
 * IMPORTANT: Mr. X's true station is NOT part of the public `GameState`. The
 * caller (e.g. a Firestore service layer) must supply it as an explicit
 * parameter to functions that need it. `legalMoves` for a detective doesn't
 * need it; `legalMoves` for Mr. X does; `applyMove` always does (even for
 * detective moves, because we may need to check for capture and we may need
 * to return the post-state mrxStation unchanged).
 *
 * `applyMove` is pure — it never mutates `state`. It returns a brand-new
 * `GameState` plus the (possibly updated) `mrxStation`.
 */

import type { DetectiveState, GameState, MrxLogEntry, Transport } from '../types'
import type { Adjacency } from './board'
import { defaultAdjacency } from './board'
import { buildLogEntry, isRevealRound } from './reveal'
import { TOTAL_ROUNDS } from './constants'
import { EngineError } from './types'
import type { DetectiveTransport, Move, MrxSubMove } from './types'
import { isOccupiedByDetective, mrxSubMoves } from './moves-internal'
import { checkWin } from './win'

export interface ApplyMoveResult {
  state: GameState
  /** Mr. X's true post-move station. Unchanged on detective moves. */
  mrxStation: number
  /**
   * Mr. X's full history of stations visited, in order. Index 0 is his
   * starting position; each `kind: 'mrx'` move appends one entry; a
   * `kind: 'mrx-double'` move appends two entries (one per sub-move).
   *
   * Returned even on detective moves (unchanged in that case) so callers
   * have a single uniform interface.
   */
  mrxStationHistory: number[]
}

// ---------------------------------------------------------------------------
// legalMoves
// ---------------------------------------------------------------------------

/**
 * Enumerate every legal `Move` the given `uid` could submit *now*.
 *
 * For Mr. X, this includes single moves and (if he has a double-move card)
 * every legal double-move pair.
 *
 * `mrxStation` is required when `uid` is Mr. X. Caller must supply it.
 */
export function legalMoves(
  state: GameState,
  uid: string,
  mrxStation?: number,
  adjacency: Adjacency = defaultAdjacency(),
): Move[] {
  if (state.status !== 'active') return []
  if (!state.awaitingUids.includes(uid)) return []

  if (uid === state.mrx.uid) {
    if (state.phase !== 'mrx') return []
    if (mrxStation === undefined) {
      throw new EngineError('INVALID_STATION', 'mrxStation required for Mr. X')
    }
    return enumerateMrxMoves(state, mrxStation, adjacency)
  }

  // Detective.
  if (state.phase !== 'detectives') return []
  const det = state.detectives.find((d) => d.uid === uid)
  if (!det || det.stranded || det.station === null) return []
  return enumerateDetectiveMoves(state, det, adjacency)
}

function enumerateDetectiveMoves(
  state: GameState,
  det: DetectiveState,
  adjacency: Adjacency,
): Move[] {
  const result: Move[] = []
  const edges = adjacency.get(det.station!) ?? []
  for (const e of edges) {
    if (e.transport === 'ferry') continue // detectives can never use ferry
    const transport = e.transport as DetectiveTransport
    if (det.tickets[transport] <= 0) continue
    if (isOccupiedByDetective(state, e.to)) continue
    result.push({
      kind: 'detective',
      uid: det.uid,
      toStation: e.to,
      transport,
    })
  }
  return result
}

function enumerateMrxMoves(
  state: GameState,
  mrxStation: number,
  adjacency: Adjacency,
): Move[] {
  const result: Move[] = []
  // Single moves.
  for (const sm of mrxSubMoves(state, mrxStation, adjacency)) {
    result.push({ kind: 'mrx', ...sm })
  }
  // Double moves: enumerate (first, second) pairs by simulating the first.
  // Not offered on the final round — a double-move needs two move slots and
  // both sub-moves count toward the 24-round clock (rules.md "Double-move
  // card"), so the latest legal double-move is round 23.
  if (state.mrx.doubleMoves > 0 && state.round < TOTAL_ROUNDS) {
    for (const first of mrxSubMoves(state, mrxStation, adjacency)) {
      // Apply the first sub-move's effects on tickets to a temporary state,
      // then enumerate seconds from `first.toStation`.
      const afterFirst = stepMrxTicketsForDouble(state, first)
      for (const second of mrxSubMoves(afterFirst, first.toStation, adjacency)) {
        result.push({ kind: 'mrx-double', first, second })
      }
    }
  }
  return result
}

/**
 * Decrement Mr. X's ticket pool to reflect that `first` has been played.
 * Used purely for enumerating legal *second* sub-moves of a double-move.
 *
 * Per rules: Mr. X's basic-transport tickets return to his pool, so the only
 * pool that actually changes between sub-moves is `blackTickets` (if the
 * first sub-move used one).
 */
function stepMrxTicketsForDouble(state: GameState, first: MrxSubMove): GameState {
  if (!first.useBlackTicket) return state
  return {
    ...state,
    mrx: { ...state.mrx, blackTickets: state.mrx.blackTickets - 1 },
  }
}

// ---------------------------------------------------------------------------
// applyMove
// ---------------------------------------------------------------------------

export interface ApplyMoveArgs {
  state: GameState
  move: Move
  /** Mr. X's true current station. Required even on detective moves. */
  mrxStation: number
  /**
   * Mr. X's full station history coming in. The engine appends to this on
   * Mr. X moves (one entry per sub-move) and returns it unchanged on
   * detective moves. Optional for backward compatibility — when omitted we
   * synthesize `[mrxStation]`. Callers that want a complete history should
   * pass the previous value (e.g. read from `private/mrx.stationHistory`
   * by the service layer, then write the result back).
   */
  mrxStationHistory?: number[]
  adjacency?: Adjacency
}

/**
 * Validate and apply a single `Move`. Throws `EngineError` on illegal moves.
 *
 * On success, returns a fresh `GameState`, the new `mrxStation` (unchanged
 * if the move was a detective move), and the new `mrxStationHistory` (with
 * one or two stations appended on Mr. X moves, unchanged on detective
 * moves).
 */
export function applyMove(args: ApplyMoveArgs): ApplyMoveResult {
  const { state, move, mrxStation, adjacency = defaultAdjacency() } = args
  const mrxStationHistory = args.mrxStationHistory ?? [mrxStation]

  if (state.status !== 'active') {
    throw new EngineError('GAME_OVER', 'Game is not active.')
  }

  switch (move.kind) {
    case 'detective':
      return applyDetectiveMove(state, move, mrxStation, mrxStationHistory, adjacency)
    case 'mrx':
      return applyMrxSingleMove(state, move, mrxStation, mrxStationHistory, adjacency)
    case 'mrx-double':
      return applyMrxDoubleMove(state, move, mrxStation, mrxStationHistory, adjacency)
    case 'detective-stranded':
      return applyDetectiveStranded(state, move.uid, mrxStation, mrxStationHistory, adjacency)
  }
}

// ---------------------------------------------------------------------------
// detective
// ---------------------------------------------------------------------------

function applyDetectiveMove(
  state: GameState,
  move: Extract<Move, { kind: 'detective' }>,
  mrxStation: number,
  mrxStationHistory: number[],
  adjacency: Adjacency,
): ApplyMoveResult {
  if (state.phase !== 'detectives') {
    throw new EngineError('WRONG_PHASE', 'Not detective phase.')
  }
  if (!state.awaitingUids.includes(move.uid)) {
    throw new EngineError('NOT_AWAITING', `Detective ${move.uid} is not awaiting a move.`)
  }
  const detIndex = state.detectives.findIndex((d) => d.uid === move.uid)
  if (detIndex < 0) throw new EngineError('UNKNOWN_PLAYER', `Unknown detective ${move.uid}.`)
  const det = state.detectives[detIndex]
  if (det.stranded) throw new EngineError('STRANDED', `Detective ${move.uid} is stranded.`)
  if (det.station === null) {
    throw new EngineError('INVALID_STATION', 'Detective has no station.')
  }
  if (det.tickets[move.transport] <= 0) {
    throw new EngineError('NO_TICKET', `Detective lacks ${move.transport} ticket.`)
  }
  // Edge exists with this transport?
  if (!hasEdge(adjacency, det.station, move.toStation, move.transport)) {
    throw new EngineError('NO_EDGE', `No ${move.transport} edge ${det.station}->${move.toStation}.`)
  }
  if (isOccupiedByDetective(state, move.toStation)) {
    throw new EngineError('OCCUPIED_BY_DETECTIVE', 'Destination occupied by another detective.')
  }

  // Apply: move detective, decrement detective ticket, append to history.
  const newDetectives = state.detectives.map((d, i) =>
    i === detIndex
      ? {
          ...d,
          station: move.toStation,
          tickets: { ...d.tickets, [move.transport]: d.tickets[move.transport] - 1 },
          stationHistory: [...(d.stationHistory ?? (d.station != null ? [d.station] : [])), move.toStation],
        }
      : d,
  )
  // NOTE: Mr. X's basic ticket counts aren't tracked in MrxPublicState (per
  // rules they're effectively unlimited). The "ticket transfer" rule is
  // therefore a no-op on the public state — but documented here for clarity.
  const nextAwaiting = state.awaitingUids.filter((u) => u !== move.uid)

  let nextState: GameState = {
    ...state,
    detectives: newDetectives,
    awaitingUids: nextAwaiting,
  }

  // After this detective moves, auto-strand any remaining awaiting detective
  // who now has zero legal moves. (Rule: stranded detectives are out for the
  // rest of the game; the engine surfaces stranding eagerly so the UI
  // doesn't have to.)
  nextState = autoStrandAwaitingDetectives(nextState, adjacency)

  // If no awaiting detectives remain, advance the round and switch to Mr. X.
  if (nextState.awaitingUids.length === 0) {
    nextState = advanceToMrxTurn(nextState)
  }

  // Win check (capture and all-stranded).
  return finalizeWithWinCheck(nextState, mrxStation, mrxStationHistory, adjacency, false)
}

function applyDetectiveStranded(
  state: GameState,
  uid: string,
  mrxStation: number,
  mrxStationHistory: number[],
  adjacency: Adjacency,
): ApplyMoveResult {
  if (state.phase !== 'detectives') {
    throw new EngineError('WRONG_PHASE', 'Not detective phase.')
  }
  if (!state.awaitingUids.includes(uid)) {
    throw new EngineError('NOT_AWAITING', `Detective ${uid} not awaiting.`)
  }
  const detIndex = state.detectives.findIndex((d) => d.uid === uid)
  if (detIndex < 0) throw new EngineError('UNKNOWN_PLAYER', `Unknown detective ${uid}.`)
  const det = state.detectives[detIndex]
  if (det.stranded) throw new EngineError('STRANDED', 'Already stranded.')

  // Verify no legal move actually exists.
  const moves = enumerateDetectiveMoves(state, det, adjacency)
  if (moves.length > 0) {
    throw new EngineError('NOT_STRANDED', `Detective ${uid} has legal moves; cannot strand.`)
  }

  const newDetectives = state.detectives.map((d, i) =>
    i === detIndex ? { ...d, stranded: true } : d,
  )
  let nextState: GameState = {
    ...state,
    detectives: newDetectives,
    awaitingUids: state.awaitingUids.filter((u) => u !== uid),
  }
  nextState = autoStrandAwaitingDetectives(nextState, adjacency)
  if (nextState.awaitingUids.length === 0) {
    nextState = advanceToMrxTurn(nextState)
  }
  return finalizeWithWinCheck(nextState, mrxStation, mrxStationHistory, adjacency, false)
}

// ---------------------------------------------------------------------------
// mrx single
// ---------------------------------------------------------------------------

function applyMrxSingleMove(
  state: GameState,
  move: Extract<Move, { kind: 'mrx' }>,
  mrxStation: number,
  mrxStationHistory: number[],
  adjacency: Adjacency,
): ApplyMoveResult {
  if (state.phase !== 'mrx') {
    throw new EngineError('WRONG_PHASE', 'Not Mr. X phase.')
  }
  if (!state.awaitingUids.includes(state.mrx.uid)) {
    throw new EngineError('NOT_AWAITING', 'Mr. X not awaiting a move.')
  }

  validateMrxSubMove(state, mrxStation, move, adjacency)

  const { state: afterFirst, mrxStation: newMrxStation } = applyMrxSubMove({
    state,
    fromStation: mrxStation,
    sub: move,
    round: state.round,
    doubleMoveSecond: false,
  })

  const newHistory = [...mrxStationHistory, newMrxStation]

  // Single move ends Mr. X's turn this round.
  const next = beginDetectivePhase(afterFirst)
  return finalizeWithWinCheck(next, newMrxStation, newHistory, adjacency, true)
}

// ---------------------------------------------------------------------------
// mrx double
// ---------------------------------------------------------------------------

function applyMrxDoubleMove(
  state: GameState,
  move: Extract<Move, { kind: 'mrx-double' }>,
  mrxStation: number,
  mrxStationHistory: number[],
  adjacency: Adjacency,
): ApplyMoveResult {
  if (state.phase !== 'mrx') {
    throw new EngineError('WRONG_PHASE', 'Not Mr. X phase.')
  }
  if (!state.awaitingUids.includes(state.mrx.uid)) {
    throw new EngineError('NOT_AWAITING', 'Mr. X not awaiting a move.')
  }
  if (state.mrx.doubleMoves <= 0) {
    throw new EngineError('NO_DOUBLE_MOVE', 'No double-move card available.')
  }
  // A double-move needs two move slots; on the final round only one remains.
  if (state.round >= TOTAL_ROUNDS) {
    throw new EngineError('NO_DOUBLE_MOVE', 'No room for a double-move on the final round.')
  }

  // Validate the first sub-move at the current station and round.
  validateMrxSubMove(state, mrxStation, move.first, adjacency)

  // Apply the first sub-move (consumes the doubleMove card up front).
  const stateAfterCardSpend: GameState = {
    ...state,
    mrx: { ...state.mrx, doubleMoves: state.mrx.doubleMoves - 1 },
  }

  const round1 = state.round
  const round2 = state.round + 1

  const first = applyMrxSubMove({
    state: stateAfterCardSpend,
    fromStation: mrxStation,
    sub: move.first,
    round: round1,
    doubleMoveSecond: false,
  })

  // After the first sub-move, increment the round counter so the second
  // sub-move's log entry uses the next round number (and reveal trigger).
  // We keep phase='mrx' between the two sub-moves.
  const beforeSecond: GameState = { ...first.state, round: round2 }

  // Validate the second sub-move at the new station.
  validateMrxSubMove(beforeSecond, first.mrxStation, move.second, adjacency)

  const second = applyMrxSubMove({
    state: beforeSecond,
    fromStation: first.mrxStation,
    sub: move.second,
    round: round2,
    doubleMoveSecond: true,
  })

  // History grows by exactly two — one entry per sub-move.
  const newHistory = [...mrxStationHistory, first.mrxStation, second.mrxStation]

  // After both sub-moves, hand off to detectives. The "current round" is the
  // second sub-move's round (round2) — that's the round detectives will move
  // within.
  const next = beginDetectivePhase(second.state)

  return finalizeWithWinCheck(next, second.mrxStation, newHistory, adjacency, true)
}

// ---------------------------------------------------------------------------
// shared helpers
// ---------------------------------------------------------------------------

function validateMrxSubMove(
  state: GameState,
  fromStation: number,
  sub: MrxSubMove,
  adjacency: Adjacency,
): void {
  if (!hasEdge(adjacency, fromStation, sub.toStation, sub.transport)) {
    throw new EngineError(
      'NO_EDGE',
      `No ${sub.transport} edge ${fromStation}->${sub.toStation}.`,
    )
  }
  if (isOccupiedByDetective(state, sub.toStation)) {
    throw new EngineError(
      'OCCUPIED_BY_DETECTIVE',
      'Destination occupied by detective.',
    )
  }
  if (sub.transport === 'ferry' && !sub.useBlackTicket) {
    throw new EngineError(
      'FERRY_REQUIRES_BLACK',
      'Ferry edges require a black ticket.',
    )
  }
  if (sub.useBlackTicket && state.mrx.blackTickets <= 0) {
    throw new EngineError('NO_BLACK_TICKET', 'No black ticket available.')
  }
  // Mr. X's basic-ticket counts are effectively unlimited (per rules.md), so
  // we don't validate them here. If you want to enforce starting counts,
  // track them in MrxPublicState and check them here.
}

interface ApplyMrxSubMoveArgs {
  state: GameState
  fromStation: number
  sub: MrxSubMove
  round: number
  doubleMoveSecond: boolean
}

function applyMrxSubMove(args: ApplyMrxSubMoveArgs): {
  state: GameState
  mrxStation: number
} {
  const { state, sub, round, doubleMoveSecond } = args

  // Build the public log entry.
  const logEntry: MrxLogEntry = buildLogEntry({
    round,
    transport: sub.useBlackTicket ? 'black' : sub.transport,
    mrxStation: sub.toStation,
    doubleMoveSecond,
  })

  const newBlackTickets = sub.useBlackTicket
    ? state.mrx.blackTickets - 1
    : state.mrx.blackTickets

  const reveal = isRevealRound(round)

  const newMrx = {
    ...state.mrx,
    transportLog: [...state.mrx.transportLog, logEntry],
    blackTickets: newBlackTickets,
    lastRevealedRound: reveal ? round : state.mrx.lastRevealedRound,
    lastRevealedStation: reveal ? sub.toStation : state.mrx.lastRevealedStation,
  }

  return {
    state: { ...state, mrx: newMrx },
    mrxStation: sub.toStation,
  }
}

function hasEdge(
  adjacency: Adjacency,
  from: number,
  to: number,
  transport: Transport,
): boolean {
  const edges = adjacency.get(from)
  if (!edges) return false
  return edges.some((e) => e.to === to && e.transport === transport)
}

/**
 * After Mr. X has finished his turn, switch to detective phase. The awaiting
 * list is every non-stranded detective whose station is not null.
 */
function beginDetectivePhase(state: GameState): GameState {
  const awaiting = state.detectives
    .filter((d) => !d.stranded && d.station !== null)
    .map((d) => d.uid)
  return {
    ...state,
    phase: 'detectives',
    awaitingUids: awaiting,
  }
}

/**
 * After all detectives have moved (or been stranded), advance to the next
 * Mr. X turn. Increments `round`.
 *
 * Note: round counter follows Mr. X's move count. After Mr. X's Nth move, the
 * detective phase happens within "round N"; once all detectives finish, we
 * bump to round N+1 for Mr. X's (N+1)th move.
 */
function advanceToMrxTurn(state: GameState): GameState {
  return {
    ...state,
    round: state.round + 1,
    phase: 'mrx',
    awaitingUids: [state.mrx.uid],
  }
}

/**
 * Walk through the awaiting list and mark any detective who has zero legal
 * moves as stranded. Eagerly removing them from `awaitingUids` keeps the UI
 * simple (it never needs to "skip" a stranded player).
 */
function autoStrandAwaitingDetectives(
  state: GameState,
  adjacency: Adjacency,
): GameState {
  let working = state
  let changed = true
  while (changed) {
    changed = false
    for (const uid of working.awaitingUids) {
      const det = working.detectives.find((d) => d.uid === uid)
      if (!det || det.stranded) continue
      if (enumerateDetectiveMoves(working, det, adjacency).length === 0) {
        working = {
          ...working,
          detectives: working.detectives.map((d) =>
            d.uid === uid ? { ...d, stranded: true } : d,
          ),
          awaitingUids: working.awaitingUids.filter((u) => u !== uid),
        }
        changed = true
      }
    }
  }
  return working
}

/**
 * Run `checkWin` and apply the resulting `winner`/`status` to the state.
 *
 * `mrxMoveJustCompleted` is true after a Mr. X move; it gates the
 * "survived 24 rounds" check.
 */
function finalizeWithWinCheck(
  state: GameState,
  mrxStation: number,
  mrxStationHistory: number[],
  adjacency: Adjacency,
  mrxMoveJustCompleted: boolean,
): ApplyMoveResult {
  // checkWin handles capture, all-stranded, mrx-cornered (when phase='mrx'
  // and !mrxMoveJustCompleted), and 24-round-survival (when
  // mrxMoveJustCompleted).
  const winner = checkWin({
    state,
    mrxStation,
    adjacency,
    mrxMoveJustCompleted,
  })
  if (winner) {
    return {
      state: {
        ...state,
        winner,
        status: winner.team === 'detectives' ? 'detectives_won' : 'mrx_won',
      },
      mrxStation,
      mrxStationHistory,
    }
  }
  return { state, mrxStation, mrxStationHistory }
}
